import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useForm} from "react-hook-form";
import {SET_IS_API_CALLED, SET_IS_API_CALLED_FINISHED} from "../../../store/features/loadingSlice";
import {
    cancelledWorkforceCase,
    createDraftWorkforceDashboardData,
    createWorkforceDashboardData,
    getDataForWorkforceDetails
} from "../../../Services/workforce";
import MeterLoggerCaseDetails from "./CaseDetails/MeterLoggerCaseDetails";
import DownloadDataManualReadCaseDetails from "./CaseDetails/DownloadDataManualReadCaseDetails";
import AlarmCaseDetails from "./CaseDetails/AlarmCaseDetails"
import CaseListingDetailsLocationDetails from "./CaseListingDetailsLocationDetails";
import {
    SET_CREATE_WORKFORCE_CASE_DETAILS,
    SET_IS_WORKFORCE_CASE_EDITABLE,
    SET_WORKFORCE_CASE_ATTACHMENT_URLS,
    SET_WORKFORCE_CASE_DETAILS
} from "../../../store/features/workforceSlice";
import EditableAlarmCaseDetails from "./CaseDetails/EditableAlarmCaseDetails";
import EditableMeterLoggerCaseDetails from "./CaseDetails/EditableMeterLoggerCaseDetails";
import EditableDownloadDataManualReadCaseDetails from "./CaseDetails/EditableDownloadDataManualReadCaseDetails";
import CaseLocationDetails from "../CreateCase/CaseLocationDetails";
import {Button} from "antd";
import NotifyCreateRecordModal from "../../../components/Modals/NotifyCreateRecordModal";
import {CANCELLABLE_CASE_STATUS, DRAFTABLE_CASE_STATUS, EDITABLE_CASE_STATUS} from "../Constants";
import {useNavigate} from "react-router-dom";
import ConditionalButton from "../../../components/form/ConditionalButton";
import CaseListingDetailsImageAttachment from "./CaseListingDetailsImageAttachment";
import NotifyActionsOnRecordModal from "../../../components/Modals/NotifyActionsOnRecordModal";

const CaseListingDetailsDetail = ({id}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {control, handleSubmit, setValue, watch, reset} = useForm();
    const [caseType, setCaseType] = useState({});
    const [caseStatusType, setCaseStatusType] = useState(0);

    useEffect(() => {
        // retrieve the details of the meter and the images
        dispatch(SET_IS_API_CALLED("Retrieving Case Details"));
        getDataForWorkforceDetails(id).then((response) => {
            setCaseType(response?.data?.caseType);
            setCaseStatusType(response?.data?.status?.id);
            dispatch(SET_WORKFORCE_CASE_DETAILS(response?.data));
            dispatch(SET_IS_WORKFORCE_CASE_EDITABLE(EDITABLE_CASE_STATUS.includes(response?.data?.status?.id)));
            dispatch(SET_CREATE_WORKFORCE_CASE_DETAILS({
                address: response?.data?.device?.address,
                // if the geocode is wrong, it could be due to it processing the logger and meter data together
                geocode: `${response?.data?.latitude ?? 0} , ${response?.data?.longitude ?? 0}`,
            }));
            sanitizeAndFormatAttachmentDataFromServer(response?.data?.attachments);

        }).finally(() => {
            dispatch(SET_IS_API_CALLED_FINISHED());
        });
    }, []);

    const sanitizeAndFormatAttachmentDataFromServer = (attachments) => {
        const sanitizedAttachmentData = attachments?.map((attachment, index) => {
            const splitUrlString = attachment?.url.split("/");
            return {
                uid: index + 1, // if we do not start at 1, then the Upload component will replace the UID
                name: splitUrlString[splitUrlString.length - 1],
                status: "done", // should be always set to done if retrieved from the server
                url: attachment?.url,
                isFromServer: 1 // need to add a flag to identify the record as server generated
            }
        });
        dispatch(SET_WORKFORCE_CASE_ATTACHMENT_URLS(sanitizedAttachmentData));
    }

    const handleRedirectToCaseListing = () => {
        navigate('/wf-case-listing');
    };

    return (
        <>
            <form>
                <div className="px-4 flex flex-col gap-5">
                    <div className={"grid grid-cols-2"}>
                        <p className="font-bold">Case Details</p>
                        <div className={"flex justify-end"}>

                            <Button
                                onClick={handleRedirectToCaseListing}
                                className="col-end-6 bg-medium-grey-100 text-white hover:bg-dark-grey"
                                type={"button"}
                                variant="solid"
                            >
                                Back
                            </Button>
                        </div>
                    </div>

                    <RetrieveCaseDetailsTypeSwitcher
                        caseType={caseType}
                        caseStatusType={caseStatusType}
                        watch={watch}
                        setValue={setValue}
                        control={control}
                        handleSubmit={handleSubmit}
                        reset={reset}
                    />


                </div>
            </form>
        </>
    );
}

const RetrieveCaseDetailsTypeSwitcher = ({caseStatusType, caseType, control, setValue, watch, handleSubmit, reset}) => {
    const dispatch = useDispatch();
    const {workforceCaseDetails} = useSelector((state) => state.Workforce);
    const [isCaseCancelled, setIsCaseCancelled] = useState(false);
    const [showNotifyActionsOnRecordModal,
        setShowNotifyActionsOnRecordModal] = useState(false);

    const onCaseCancel = () => {
        setShowNotifyActionsOnRecordModal(true);
    }

    const handleCaseCancel = () => {
        setIsCaseCancelled(true);
        dispatch(SET_IS_API_CALLED("Cancelling the case"));
        cancelledWorkforceCase(workforceCaseDetails?.id)
            .finally(() => {
                dispatch(SET_IS_API_CALLED_FINISHED());
            });
    }

    return (
        <>
            <NotifyActionsOnRecordModal
                showNotifyActionsOnRecordModal={showNotifyActionsOnRecordModal}
                setShowNotifyActionsOnRecordModal={setShowNotifyActionsOnRecordModal}
                modalTitle={"Cancel Case"}
                modalText={`Are you sure you want to cancel this case?`}
                handleAction={() => handleCaseCancel()}
            />
            {!EDITABLE_CASE_STATUS.includes(caseStatusType) ?
                <CaseDetailsViewOnlyTypeSwitcher caseType={caseType} control={control} onCaseCancel={onCaseCancel}
                                                 isCaseCancelled={isCaseCancelled}
                                                 setIsCaseCancelled={setIsCaseCancelled}/> :
                <CaseDetailsEditableTypeSwitcher caseType={caseType} watch={watch} setValue={setValue} control={control}
                                                 handleSubmit={handleSubmit} reset={reset} onCaseCancel={onCaseCancel}
                                                 isCaseCancelled={isCaseCancelled}
                                                 setIsCaseCancelled={setIsCaseCancelled}
                />}
        </>
    )
}

const CaseDetailsViewOnlyTypeSwitcher = ({caseType, control, onCaseCancel, isCaseCancelled, setIsCaseCancelled}) => {
    const {workforceCaseDetails} = useSelector((state) => state.Workforce);

    // TODO need to change this to something more suitable
    const contentMap = [
        {
            caseType: 1,
            contentComponent: <AlarmCaseDetails/>,
        },
        {
            caseType: 2,
            contentComponent: <MeterLoggerCaseDetails/>,
        },
        {
            caseType: 3,
            contentComponent: <DownloadDataManualReadCaseDetails/>,
        },
    ];


    return (
        <>

            {setIsCaseCancelled && <NotifyCreateRecordModal
                modalTitle={"Case Cancelled"}
                modalText={`Your case has been cancelled.`}
                setShowNotifyCreateRecordModal={setIsCaseCancelled}
                showNotifyCreateRecordModal={isCaseCancelled}
                redirectUrl={"/wf-case-listing"}
                timeout={1000}
            />}

            {contentMap
                ?.filter((content) => {
                    if (caseType?.id === 1) {
                        return content.caseType === 1;
                    } else if (caseType?.id >= 2 && caseType?.id <= 6) {
                        return content.caseType === 2;
                    } else {
                        return content.caseType === 3;
                    }
                })
                .map((content) => content.contentComponent)
            }

            <CaseListingDetailsImageAttachment control={control} disabled={true}/>
            <CaseListingDetailsLocationDetails control={control}/>
            {CANCELLABLE_CASE_STATUS.find(status => workforceCaseDetails?.status?.id === status) &&
                <div className="pt-4 grid grid-cols-5 gap-4">
                    <Button
                        onClick={onCaseCancel}
                        className="col-end-6 bg-dark-blue-200 text-white"
                        type={"button"}
                        color="danger" variant="solid"
                    >
                        Cancel
                    </Button>
                </div>}
        </>
    );
};

const CaseDetailsEditableTypeSwitcher = ({
                                             caseType,
                                             control,
                                             setValue,
                                             watch,
                                             handleSubmit,
                                             reset,
                                             onCaseCancel,
                                             isCaseCancelled,
                                             setIsCaseCancelled
                                         }) => {
    const dispatch = useDispatch();
    const {workforceCaseDetails, attachmentUrls, removedAttachmentUrls} = useSelector((state) => state.Workforce);
    const [showNotifyCreateRecordModal, setShowNotifyCreateRecordModal] = useState(false);
    // TODO need to change this to something more suitable
    const contentMap = [
        {
            caseType: 1,
            contentComponent: <EditableAlarmCaseDetails control={control} setValue={setValue} watch={watch}
                                                        reset={reset}/>,
        },
        {
            caseType: 2,
            contentComponent: <EditableMeterLoggerCaseDetails control={control} setValue={setValue} watch={watch}
                                                              reset={reset}/>,
        },
        {
            caseType: 3,
            contentComponent: <EditableDownloadDataManualReadCaseDetails control={control} setValue={setValue}
                                                                         watch={watch} reset={reset}/>,
        },
    ];

    const handleDataWithGeocode = (data, callToApi) => {
        if (data?.geocode !== undefined && data?.geocode !== null) {
            const splitGeocode = data.geocode.toString().trim().split(",").map(codes => codes.trim());

            const sanitizedAttachmentUrl = attachmentUrls?.map(attachment => attachment?.url);


            dispatch(SET_IS_API_CALLED("Creating New Case"));
            callToApi(data?.id, {
                "attachmentUrls": sanitizedAttachmentUrl,
                "attachmentsToRemove": removedAttachmentUrls,
                "alarmTypeId": data?.alarmTypeId,
                "region": data?.region,
                "caseTypeId": data?.caseTypeId,
                "deviceId": data?.deviceId,
                "fullAddress": data?.fullAddress,
                "latitude": splitGeocode[0] ?? 0.0,
                "longitude": splitGeocode[1] ?? 0.0,
                "technicianGroupId": data?.technicianGroupId,
            }).then((response) => {
                if (response?.status < 400) {
                    setShowNotifyCreateRecordModal(true);
                }
                //
            }).finally(() => {
                dispatch(SET_IS_API_CALLED_FINISHED());
            });
        }
    }

    const onSubmit = (data) => {
        handleDataWithGeocode(data, createWorkforceDashboardData);
    }

    const onDraftSubmit = (data) => {
        handleDataWithGeocode(data, createDraftWorkforceDashboardData);
    }


    return (
        <>
            {showNotifyCreateRecordModal && <NotifyCreateRecordModal
                modalTitle={workforceCaseDetails?.status?.id === 2 ? "Case Assigned" : "Case Edited"}
                modalText={`Your case has been ${workforceCaseDetails?.status?.id === 2 ? "assigned" : "edited"}.`}
                setShowNotifyCreateRecordModal={setShowNotifyCreateRecordModal}
                showNotifyCreateRecordModal={showNotifyCreateRecordModal}
                redirectUrl={"/wf-case-listing"}
                timeout={1000}
            />}

            {setIsCaseCancelled && <NotifyCreateRecordModal
                modalTitle={"Case Cancelled"}
                modalText={`Your case has been cancelled.`}
                setShowNotifyCreateRecordModal={setIsCaseCancelled}
                showNotifyCreateRecordModal={isCaseCancelled}
                redirectUrl={"/wf-case-listing"}
                timeout={1000}
            />}

            {contentMap
                ?.filter((content) => {
                    if (caseType?.id === 1) {
                        return content.caseType === 1;
                    } else if (caseType?.id >= 2 && caseType?.id <= 6) {
                        return content.caseType === 2;
                    } else {
                        return content.caseType === 3;
                    }
                })
                .map((content) => content.contentComponent)
            }
            <CaseListingDetailsImageAttachment control={control} disabled={false}/>
            <CaseLocationDetails control={control} handleSubmit={handleSubmit} setValue={setValue}/>
            <div
                className={`pt-4 grid gap-4 grid-cols-5`}>
                <ConditionalButton
                    handleOnClick={handleSubmit(onDraftSubmit)}
                    className={`bg-dark-blue-200 text-white ${CANCELLABLE_CASE_STATUS.find(status => workforceCaseDetails?.status?.id === status) ? "col-end-4" : "col-end-5"}`}
                    isVisible={DRAFTABLE_CASE_STATUS.find(status => workforceCaseDetails?.status?.id === status)}>
                    Draft
                </ConditionalButton>
                <ConditionalButton handleOnClick={handleSubmit(onSubmit)}
                                   className={`bg-dark-blue-200 text-white ${CANCELLABLE_CASE_STATUS.find(status => workforceCaseDetails?.status?.id === status) ? "col-end-5" : "col-end-6"}`}
                                   isVisible={EDITABLE_CASE_STATUS.find(status => workforceCaseDetails?.status?.id === status)}>
                    {workforceCaseDetails?.status?.id === 2 ? "Assign" : "Edit"}
                </ConditionalButton>
                <ConditionalButton
                    handleOnClick={handleSubmit(onCaseCancel)}
                    className={"col-end-6 text-white"}
                    color={"danger"} variant={"solid"}
                    isVisible={CANCELLABLE_CASE_STATUS.find(status => workforceCaseDetails?.status?.id === status)}
                >
                    Cancel
                </ConditionalButton>
            </div>
        </>
    );
}

export default CaseListingDetailsDetail;